import { useEffect, useRef, useState } from "react";
import { Outlet, useMatches, useNavigate, useLocation } from "react-router-dom";
import { Menu } from "./pages/component/menu";
import { Header } from "ui";
import { router } from "./router.jsx";
import { useSelector, useDispatch } from "react-redux";
import { getUser } from "./selectors/commonSelectors";
import { User } from "./features/user";
import {
  openSocialAccountConnectDialog,
  SocialAccountConnect,
} from "./features/social-account-connect";
import { DueInvoicesBanner } from "./components/DueInvoicesBanner";
import { SubscriptionBanner } from "./components/SubscriptionBanner";
import {
  ServiceStatusBanner,
  ServiceStatusProvider,
} from "./features/service-status";
import { showModalsIfNeeded } from "./libs/modals/init";
import { checkAndShowDraftRestoration } from "./features/post/bulk-create/draft-restoration";
import { useTosStatusCheck } from "./hooks/useTosStatusCheck";
import { TosAcceptanceDialog } from "./components/TosAcceptanceDialog";
import { useAccountsNeedingReconnection } from "./hooks/useAccountsNeedingReconnection";
import { AccountReconnectionDialog } from "./components/AccountReconnectionDialog";
import ModalRoot from "./components/ModalRoot/ModalRoot";
import { cn } from "@/lib/utils";
/**
 * Extract GA4 client_id from the _ga cookie.
 * Inlined here to avoid importing analytics-init.ts (which eagerly pulls in
 * the full @amplitude SDK). The canonical copy lives in analytics-init.ts.
 */
function getGaClientId(): string | undefined {
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(.+?)(?:;|$)/);
  return match?.[1];
}
import { resetSessionState } from "./api/_tokenRefresh";

export default function RootComponent(): JSX.Element {
  const match = useMatches();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user: User = useSelector(getUser);
  const currentUserId = user.userId;

  // Check TOS status and show modal if needed
  const { showTosModal, onTosAccepted } = useTosStatusCheck(user);

  // Check if any accounts need reconnection
  const {
    accountsNeedingReconnection,
    hasAccountsNeedingReconnection,
    isDismissed,
  } = useAccountsNeedingReconnection();

  // State to track if user has dismissed the reconnection dialog this session
  const [reconnectDialogDismissed, setReconnectDialogDismissed] =
    useState(false);

  // Check if bulk create modal is being auto-opened
  const params = new URLSearchParams(window.location.search);
  const isAutoOpeningBulkCreate = params.get("autoOpenBulkCreate") === "true";
  const isAdminRoute = /^\/admin(?:-v2)?(?:\/|$)/.test(
    window.location.pathname
  );

  // Show reconnection dialog if:
  // 1. There are accounts needing reconnection
  // 2. User hasn't dismissed it for a week (localStorage)
  // 3. User hasn't dismissed it this session
  // 4. TOS modal is not showing (TOS takes priority)
  // 5. Bulk create modal is not being auto-opened
  const showReconnectDialog =
    hasAccountsNeedingReconnection &&
    !isDismissed &&
    !reconnectDialogDismissed &&
    !showTosModal &&
    !isAutoOpeningBulkCreate &&
    !isAdminRoute; // Admin users won't see the reconnection dialog since they don't manage social accounts

  // FontAwesome kit is loaded via <script defer> in the shared app shell HTML to
  // start the download earlier (in parallel with JS bundles). The old dynamic
  // injection was removed to eliminate the late-loading FOUC.

  useEffect(() => {
    /** match.length === 1 means matching root level / */
    if (currentUserId && match.length === 1) {
      /** Navigate immediately to dashboard on root path */
      navigate(`/dashboard${window.location.search}`);
    }
  }, [currentUserId, match.length, navigate]);

  useEffect(() => {
    if (currentUserId) {
      showModalsIfNeeded();
      checkAndShowDraftRestoration(dispatch, currentUserId);
    }
  }, [dispatch, currentUserId]);

  // Fire once per Root mount: wait until `match.length > 1` so the
  // root-redirect effect has flushed (otherwise Page Path captures as `/`),
  // and the ref keeps it from re-firing as the user navigates between
  // `/dashboard/*` sub-routes (which change `match.length`).
  const dashboardLoadedFiredRef = useRef(false);
  useEffect(() => {
    if (
      !currentUserId ||
      match.length === 1 ||
      dashboardLoadedFiredRef.current
    ) {
      return;
    }
    dashboardLoadedFiredRef.current = true;
    const gaClientId = getGaClientId();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dashboard_loaded",
      user_id: currentUserId,
      ...(gaClientId ? { user_pseudo_id: gaClientId } : {}),
    });
  }, [currentUserId, match.length]);

  // Logout handler - redirects to logout page
  const handleLogout = () => {
    window.location.href = "/logout";
  };

  const location = useLocation();

  // Reset the auth session-invalid flag on navigation so retries are possible
  // after a route change (e.g. user logs back in via another tab).
  useEffect(() => {
    resetSessionState();
  }, [location.pathname]);

  const ncyBreadcrumb = (router.state.matches[1]?.route as any)?.ncyBreadcrumb;
  const headerVariant = (router.state.matches[1]?.route as any)?.headerVariant;

  // Support dynamic breadcrumbs via getLabel/getSubLabel functions
  const pageTitle =
    ncyBreadcrumb?.getLabel?.(location.pathname) ?? ncyBreadcrumb?.label;
  const subTitle =
    ncyBreadcrumb?.getSubLabel?.(location.pathname) ?? ncyBreadcrumb?.subLabel;

  return (
    // One shared SSE connection to status.skedsocial.com for the whole shell:
    // the banner below and the header dot both read from this provider.
    <ServiceStatusProvider>
      <div className="flex w-screen h-screen overflow-hidden bg-blue-37">
        <Menu />
        <div
          className={cn(
            "dashboard-content-panel flex-1 bg-blue-25 min-h-screen rounded-xl mt-2 mr-2 mb-2 overflow-y-auto shadow-[var(--shadow-panel-left)]",
            headerVariant === "calendar-uplift" && "ml-4"
          )}
        >
          <Header
            variant={headerVariant}
            pageTitle={pageTitle}
            subTitle={subTitle}
            logout={handleLogout}
            customLinkInTitle={ncyBreadcrumb?.customLinkInTitle}
            openSocialAccountConnectDialog={openSocialAccountConnectDialog}
          />
          {/* Service status first: an incident outranks any billing message. */}
          <ServiceStatusBanner />
          <DueInvoicesBanner />
          <SubscriptionBanner />
          <Outlet />
        </div>
        {/* NOTE: move SocialAccountConnect dialog into the root dialog stack when that UI flow is rewritten */}
        <SocialAccountConnect />
      </div>

      {/* TOS Acceptance Modal - shown when user needs to accept updated Terms of Service */}
      <TosAcceptanceDialog open={showTosModal} onAccepted={onTosAccepted} />

      {/* Account Reconnection Modal - shown when accounts need reconnection */}
      <AccountReconnectionDialog
        open={showReconnectDialog}
        accountsNeedingReconnection={accountsNeedingReconnection}
        onDismiss={() => setReconnectDialogDismissed(true)}
      />

      {/* Global modal root for redux-managed modals (bulk-create, media picker, etc.) */}
      <ModalRoot />
    </ServiceStatusProvider>
  );
}
